/**
 * src/shell/copy.ts — 全部玩家可见文案集中地。
 *
 * 文案已定稿：内容编剧 deck v1.2（docs/copy-deck-v1.md §一，151 key 全覆盖，
 * v1.1 于 2026-08-13 落地、v1.2 增量于 2026-08-14 落地）。改文案先改 deck，本文件只跟随 deck。
 * 本文件是唯一文案出处——UI 组件不自带文案字符串（v1.2 起含 §四低保全文与 sandbox 分节，
 * 组件内常量 BAILOUT_STORY 已迁回，无例外）。
 */

export const COPY = {
  // ── S0 标题/开场（设计稿 §2 Phase 0；剧情全文 = deck §三）──
  title: {
    logo: '第三次玩 · Third Play',
    start: '开始上班',
    // 两屏剧情（deck §三）：数组两段 = 第 1 屏 / 第 2 屏，段内 \n\n 分自然段
    intro: [
      '你，二十七岁，一家上市公司总裁的独生子。\n职业：尼特族。特长：打游戏。\n\n上个月，父亲收购了一家快倒闭的小游戏公司，把你叫进办公室，只扔下一句话：\n\n「ThirdPlay，归你了。做出名堂，或者回来上班。」',
      '这里是你的办公室。\n\n你的游戏制作总监生涯，正式开始。',
    ],
    introContinue: '继续',
    introSkip: '跳过',
    keyTitle: '接通线路：Moonshot API Key',
    keyHint:
      '员工们靠这条线路干活，话费从你的 Key 里出。建议单开一把专 Key、小额充值、随时可吊销——就像给公司开一张限额电话卡。',
    keyModeLocal: '记住这把钥匙（本机长期有效）',
    keyModeSession: '只用这一次（关页就忘）',
    keyEnter: '进入公司',
    keyLater: '暂不设置，先进公司看看',
    keySaved: '线路接上了，员工随时能开工。',
  },

  // ── S1 驾驶舱主屏（设计稿 §1.2 S1 / §4.3）──
  cockpit: {
    objects: {
      computer: '电脑',
      papers: '文件堆',
      phone: '电话',
      shelf: '卡带架',
    },
    modeMinimal: '极简',
    modeExpert: '专家（敬请期待）',
    modeExpertTip: '专家模式尚未开放（预留接口）。',
    pendingDecisions: (n: number) => `${n} 份文件等你拍板`,
    pendingPublish: (n: number) => `${n} 盘卡带等你上架`,
    emptyPapers: '桌面干净，没有待批的文件',
  },

  // ── 通用导航 ──
  nav: {
    backToDesk: '← 回到桌前',
    hangUp: '挂电话',
    back: '← 返回',
  },

  // ── S2 电脑 · 公司终端（设计稿 §1.2 S2）──
  computer: {
    tabProjects: '项目记录',
    tabLedger: '经营账本',
    tabSettings: '系统设置',
    emptyProjects: '项目记录是空的。点「新立项」，给公司开第一单。',
    newProject: '新立项',
    status: {
      drafting: '待开工',
      awaiting_decision: '待拍板',
      developing: '开发中',
      in_review: '待验收',
      completed: '已上架',
    } as Record<string, string>,
    startWork: '开工',
    goDecide: '去拍板',
    decisionProgress: (done: number, total: number) => `决策 ${done}/${total} 已拍板`,
    startDevelopment: '开始开发',
    submitPublish: '提交上架',
    publishConfirm: '送审后不可撤回——评审一开场，成败就交给市场了。确认上架？',
    confirm: '确认',
    cancel: '再想想',
    abandon: '放弃项目',
    abandonConfirm: '放弃后这个项目将物理删除：不额外扣资金，但已经投入的一切就此作废。确认放弃？',
    viewReview: '查看评审报告',
    play: '试玩',
    ledger: {
      funds: '资金',
      level: '等级',
      exp: '经验',
      tokenTotal: '累计 token 消耗',
      bailout: '天使投资人援助次数',
      projectsDone: '已上架项目',
      breakEven: '盈亏平衡表',
      colProject: '项目',
    },
  },

  // ── S3 决策屏（设计稿 §1.2 S3 / §2 Phase 3）──
  decisions: {
    empty: '文件堆是空的——没有等你拍板的文件。',
    listTitle: '待批文件',
    from: (name: string) => `呈报人：${name}`,
    styleBadge: {
      safe: '稳妥项',
      bold: '激进项',
      signature: '私货项',
    } as Record<string, string>,
    impact: '选了它会怎样',
    confirm: '就这么定',
    resolved: '已拍板',
    allResolved: '全部拍板完毕',
    accidentTitle: '开发事故 · 制作人三选一',
    accidentRetry: '再派一轮',
    accidentDefective: '残次品上架（评审会给事故分——丢人，但能回一点血）',
    accidentAbandon: '放弃项目',
  },

  // ── S4 电话 · 部门（设计稿 §1.2 S4）──
  phone: {
    dialTitle: '拨号',
    finance: '财务部',
    hr: '人事部',
    market: '市场部',
    project: '项目部',
    hrDept: {
      hired: '在职员工',
      candidates: '人才市场',
      hire: '录用',
      fire: '解雇',
      fireConfirm: (name: string) => `确认解雇 ${name}？`,
      roleFull: '这个岗位已经有人了（每岗限 1 人）。要换人，先解雇再录用。',
      hiredToast: (name: string) => `${name}到岗了。`,
      inProject: '项目开发中',
    },
    financeDept: {
      title: '资金状况',
      reviewTable: '项目收支记录',
    },
    marketDept: {
      season: '当季',
      hot: '热门题材',
      cold: '冷门雷区',
      mechanics: '热门机制',
      editorial: '编辑部点评',
    },
    projectDept: {
      noActive: '当前没有在研项目。',
      phase: '当前阶段',
      feed: '现场播报',
      queue: '队列中的指令',
    },
  },

  // ── S5 卡带架 · 图鉴（设计稿 §1.2 S5）──
  shelf: {
    empty: '卡带架还空着。第一盘卡带，等第一个项目上架。',
    reviewMissing: '这盘卡带的评审档案找不到了（镇店老存档）。',
  },

  // ── S6 立项屏（设计稿 §1.2 S6 / §2 Phase 2）──
  newProject: {
    title: '新立项',
    ideaLabel: '用一段话描述你想做的游戏',
    ideaPlaceholder: '例：复古街机风的打砖块……越具体，策划砍得越准。',
    teamTitle: '开发团队（在岗自动成队）',
    teamMissing: '团队不齐——去人事部把策划、程序、美术招满再立项。',
    costEstimate: '预计开发成本',
    tokenEstimate: '预计 token 量级',
    submit: '提交立项',
    ideaRequired: '先写一段话再提交。',
  },

  // ── S7 评审报告（设计稿 §1.2 S7）──
  review: {
    title: '评审报告',
    dimensions: {
      completion: '完成度',
      creativity: '创意',
      art: '美术',
      game_feel: '手感',
      theme_fit: '题材契合',
    } as Record<string, string>,
    weighted: '加权总分',
    revenue: '收入',
    cost: '成本',
    profit: '盈亏',
    exp: '经验',
    marketFit: '市场契合点评',
    overall: '总评',
  },

  // ── 结算简报浮层（设计稿 §2 Phase 6；§四全文见 SettlementOverlay 扩展区）──
  settlement: {
    title: '结算简报',
    levelUp: (lv: number) => `公司升到 Lv.${lv} 了。`,
    bailout: (n: number) => `资金见底——天使投资人第 ${n} 次注资，账上补回 300 金币。`,
    bailoutStoryTitle: '深夜来电',
    // 低保事件正文（deck §四单版通用，${n} = times_used；结算浮层扩展区承载）
    bailoutStory: (n: number) =>
      `深夜，陌生号码。接起来，是父亲。\n\n「就知道你小子又亏本了。资金马上打到你账上——第 ${n} 次了，好自为之。」\n\n挂了。\n\n忙音里，你好像听见电话那头一声很轻的叹息。\n\n账上补回 300 金币。`,
    viewReport: '查看评审报告',
    close: '回到桌前',
  },

  // ── 系统设置（设计稿 §1.2 S2 系统设置）──
  settings: {
    keySection: '线路（API Key）',
    keyStatusOn: '线路已接通',
    keyStatusOff: '未接线',
    keyClear: '拔掉钥匙（一键清除）',
    saveSection: '存档',
    export: '导出存档',
    exported: (name: string) => `已导出：${name}`,
    import: '导入存档',
    importOk: '导入成功，存档已替换。',
    importFail: '导入被拒绝：',
    restore: '回滚到最近一次备份',
    restored: '已回滚。',
    noBackup: '没有可用备份。',
    reset: '重置存档（回到出厂状态）',
    resetConfirm: '重置会清空全部进度——Key 保留，其余一切归零。确认？',
    resetDone: '已重置。',
  },

  // ── 事件模板（系统类事件按模板渲染，评审对齐项 1）──
  eventTpl: {
    phase: (detail: string) => `【阶段】${detail}`,
    audit: (key: string, fields: string) => `【存档】${key}：${fields}`,
    warn: (text: string) => `【提醒】${text}`,
    error: (text: string) => `【事故】${text}`,
    gateEnter: '【门禁】等你拍板',
    gateExit: '【门禁】已解除',
    wakeup: (action: string, transition: string) => `【指令】${action} → ${transition}`,
    stream: (stage: string, received: number) => `【开发中】${stage} · 已接收 ${received} 字符`,
  },

  // ── 壳层提示 ──
  shell: {
    errorPrefix: '出事了：',
    resumeFail: '恢复存档时出了问题，细节见控制台。',
    writeFail: '写入存档失败（存储空间可能不足），刚才的操作未生效。',
  },

  // ── S8 试玩沙箱（deck v1.2 §15，E1 补收）──
  sandbox: {
    missing: '这盘卡带读不出来（产物不在库中）。',
    loading: '卡带读取中……',
  },

  graybox: '灰盒版 · 文案已定稿（deck v1）· 驾驶舱底图已接入（设计稿 §4.4）',
} as const
